import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ClipboardPlus, Link2, Loader2, Upload, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { training, studies, studyStaff, ApiError, STUDY_STAFF_ROLES } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { useAuth } from '@/contexts/AuthContext';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { studyLabel } from '@/lib/studyLabel';

export interface NewTrainingDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const ALLOWED_MIME = new Set(['application/pdf']);
const MAX_MB = 50;

function guessMime(_name: string): string {
  return 'application/pdf';
}

async function uploadWithProgress(
  url: string,
  file: File,
  mime: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

/**
 * Standalone/external training — replaces the old binder-routed
 * AssignTrainingDialog.tsx entirely (client, 14 Aug demo-review call:
 * "Not in binder... this is only on training record, training table").
 * No zone/section/artifact/document-type picker, no
 * binder.createSlot/presignVersion/finaliseVersion chain — a training
 * item here is completely decoupled from the eISF binder.
 *
 * Does not go live to trainees on submit — goes to the study PI for
 * approval first (see ApprovalNeededPage.tsx's "Training approvals"
 * section). Certifications are only generated once approved.
 */
export function NewTrainingDialog({ open, onClose, onCreated }: NewTrainingDialogProps) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const siteId = user?.activeSiteId ?? '';

  const [studyId, setStudyId] = useState('');
  const [trainingName, setTrainingName] = useState('');
  const [kind, setKind] = useState<'internal' | 'external'>('internal');
  const [targetDate, setTargetDate] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setStudyId('');
      setTrainingName('');
      setKind('internal');
      setTargetDate('');
      setExternalLink('');
      setFile(null);
      setUploadProgress(null);
      setUserIds([]);
      setRoles([]);
      createMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: () => studies.list(),
    enabled: open,
    staleTime: 30_000,
  });
  const studyOptions = studiesQ.data?.rows ?? [];

  const staffQ = useQuery({
    queryKey: ['study-staff', studyId],
    queryFn: () => studyStaff.list(studyId),
    enabled: open && !!studyId,
    staleTime: 30_000,
  });
  const staffOptions: MultiSelectOption[] = [
    ...(staffQ.data?.assignments ?? []).map((a) => ({
      value: a.user_id,
      label: a.full_name,
      sublabel: `${a.email} · ${humanRole(a.role)}`,
    })),
    // Monitors/auditors from Monitor Assignments: never trained automatically
    // (13 Oct 2026) — picking them here is how they get a training.
    ...(staffQ.data?.reviewers ?? [])
      .filter((r) => !(staffQ.data?.assignments ?? []).some((a) => a.user_id === r.user_id))
      .map((r) => ({
        value: r.user_id,
        label: r.full_name,
        sublabel: `${r.email} · ${humanRole(r.role)} (monitor assignment)`,
      })),
  ];
  const roleOptions: MultiSelectOption[] = STUDY_STAFF_ROLES.map((r) => ({ value: r, label: humanRole(r) }));

  function handleFile(f: File) {
    if (!ALLOWED_MIME.has(f.type || guessMime(f.name))) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    if (f.size === 0) {
      toast.error('Cannot upload an empty file.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  const createMut = useMutation({
    mutationFn: async () => {
      if (!studyId) throw new Error('Pick a study.');
      if (!trainingName.trim()) throw new Error('Give this training a name.');
      if (kind === 'external' && !externalLink.trim()) throw new Error('Enter the external training link.');
      if (kind === 'internal' && !file) throw new Error('Choose a file to upload.');
      if (userIds.length === 0 && roles.length === 0) {
        throw new Error('Pick at least one staff member or role.');
      }

      let source: { s3_bucket: string; s3_key: string; file_name: string; file_size: number; mime_type: string } | undefined;
      if (kind === 'internal' && file) {
        const mime = file.type || guessMime(file.name);
        const presigned = await training.manual.presignUpload({ file_name: file.name, mime_type: mime });
        await uploadWithProgress(presigned.upload_url, file, mime, setUploadProgress);
        source = {
          s3_bucket: presigned.s3_bucket,
          s3_key: presigned.s3_key,
          file_name: file.name,
          file_size: file.size,
          mime_type: mime,
        };
      }

      return training.manual.create({
        study_id: studyId,
        site_id: siteId,
        training_name: trainingName.trim(),
        training_kind: kind,
        ...(targetDate ? { target_completion_date: new Date(targetDate).toISOString() } : {}),
        ...(kind === 'external' ? { external_link: externalLink.trim() } : {}),
        ...(source ? { source } : {}),
        ...(userIds.length > 0 ? { user_ids: userIds } : {}),
        ...(roles.length > 0 ? { roles } : {}),
      });
    },
    onSuccess: () => {
      toast.success('Training created — sent to the study PI for approval.');
      qc.invalidateQueries({ queryKey: ['training'] });
      qc.invalidateQueries({ queryKey: ['me', 'tasks'] });
      onCreated();
      onClose();
    },
    onError: (err) => {
      setUploadProgress(null);
      toast.error(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to create training',
      );
    },
  });

  if (!open) return null;

  const isWorking = createMut.isPending;
  const isValid =
    !!studyId &&
    !!trainingName.trim() &&
    (kind === 'internal' ? !!file : !!externalLink.trim()) &&
    (userIds.length > 0 || roles.length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isWorking) onClose();
      }}
    >
      <div className="card w-full max-w-lg space-y-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-primary">
              <ClipboardPlus className="h-4 w-4" />
              New training
            </h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Goes to the study PI for approval before it reaches trainees.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isWorking}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label text-xs" htmlFor="new-training-study">
            Study *
          </label>
          <div className="relative">
            <select
              id="new-training-study"
              className="input w-full appearance-none pr-8"
              value={studyId}
              disabled={isWorking || studiesQ.isLoading}
              onChange={(e) => setStudyId(e.target.value)}
            >
              <option value="" disabled>
                {studiesQ.isLoading ? 'Loading studies…' : 'Pick a study…'}
              </option>
              {studyOptions.map((s) => (
                <option key={s.study_id} value={s.study_id}>
                  {studyLabel(s)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-ink-muted" />
          </div>
        </div>

        <div>
          <label className="label text-xs" htmlFor="new-training-name">
            Training name *
          </label>
          <input
            id="new-training-name"
            className="input"
            maxLength={200}
            value={trainingName}
            disabled={isWorking}
            placeholder="e.g. Sponsor safety notice — Protocol amendment 3"
            onChange={(e) => setTrainingName(e.target.value)}
          />
        </div>

        <div>
          <label className="label text-xs">Target completion date (optional)</label>
          <input
            type="date"
            className="input"
            value={targetDate}
            disabled={isWorking}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </div>

        <div className="flex gap-1 rounded-lg border border-surface-border bg-surface-muted/30 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setKind('internal')}
            disabled={isWorking}
            className={`flex-1 rounded-md px-2 py-1.5 font-medium transition-colors ${
              kind === 'internal' ? 'bg-white text-ink-primary shadow-sm' : 'text-ink-muted hover:text-ink-primary'
            }`}
          >
            <Upload className="mr-1 inline h-3.5 w-3.5" />
            Internal (upload a file)
          </button>
          <button
            type="button"
            onClick={() => setKind('external')}
            disabled={isWorking}
            className={`flex-1 rounded-md px-2 py-1.5 font-medium transition-colors ${
              kind === 'external' ? 'bg-white text-ink-primary shadow-sm' : 'text-ink-muted hover:text-ink-primary'
            }`}
          >
            <Link2 className="mr-1 inline h-3.5 w-3.5" />
            External (LMS link)
          </button>
        </div>

        {kind === 'internal' ? (
          <div>
            <label className="label text-xs">Document *</label>
            <div
              className={`relative flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                dragOver
                  ? 'border-accent-500 bg-accent-50'
                  : isWorking
                    ? 'cursor-not-allowed border-surface-border opacity-50'
                    : 'cursor-pointer border-surface-border hover:border-accent-400 hover:bg-surface-muted/40'
              }`}
              onClick={() => !isWorking && fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (!isWorking) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                if (!isWorking) handleDrop(e);
                else e.preventDefault();
              }}
            >
              {isWorking ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-accent-500" />
                  <p className="text-xs font-medium text-ink-primary">
                    {uploadProgress !== null ? `Uploading… ${uploadProgress}%` : 'Working…'}
                  </p>
                </>
              ) : file ? (
                <>
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                  <p className="text-xs font-medium text-ink-primary">{file.name}</p>
                  <p className="text-[11px] text-ink-muted">Click or drop to replace</p>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-ink-muted" />
                  <p className="text-xs font-medium text-ink-primary">
                    Drag a file here or <span className="text-accent-700 underline">browse</span>
                  </p>
                  <p className="text-[11px] text-ink-muted">PDF only, max {MAX_MB} MB</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = '';
                }}
                disabled={isWorking}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="label text-xs" htmlFor="new-training-link">
              External training link *
            </label>
            <input
              id="new-training-link"
              type="url"
              className="input"
              value={externalLink}
              disabled={isWorking}
              placeholder="https://your-lms.example.com/course/123"
              onChange={(e) => setExternalLink(e.target.value)}
            />
          </div>
        )}

        <div>
          <label className="label text-xs">Staff members</label>
          <MultiSelectSearch
            options={staffOptions}
            value={userIds}
            onChange={setUserIds}
            placeholder={!studyId ? 'Pick a study first' : staffQ.isLoading ? 'Loading staff…' : 'Search staff…'}
          />
        </div>

        <div>
          <label className="label text-xs">Or by role — every staff member currently delegated that role</label>
          <MultiSelectSearch
            options={roleOptions}
            value={roles}
            onChange={setRoles}
            placeholder={!studyId ? 'Pick a study first' : 'Search roles…'}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={isWorking}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!isValid || isWorking}
            onClick={() => createMut.mutate()}
          >
            {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardPlus className="h-4 w-4" />}
            Create training
          </button>
        </div>
      </div>
    </div>
  );
}
